import { Gift, Tag, Truck, Star, Mail, Award, Ticket } from 'lucide-react'
import { BenefitStatusBadge } from '@/modules/benefits/components/BenefitStatusBadge'
import type { ClientBenefitHistoryItem, BenefitVoucher, AvailableBenefitTier, ActionType } from '@/modules/benefits/types'
import { ACTION_TYPE_LABELS } from '@/modules/benefits/types'

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getActionIcon(actionType: ActionType) {
  switch (actionType) {
    case 'coupon':
      return <Tag size={14} className="text-purple-600" />
    case 'physical_gift_voucher':
      return <Gift size={14} className="text-pink-600" />
    case 'free_shipping_flag':
      return <Truck size={14} className="text-blue-600" />
    case 'points_bonus':
      return <Star size={14} className="text-yellow-600" />
    case 'email_notification':
      return <Mail size={14} className="text-gray-600" />
  }
}

function getMetadataDisplay(benefit: ClientBenefitHistoryItem): string {
  const meta = benefit.metadata
  switch (benefit.action_type) {
    case 'coupon': {
      const code = meta.coupon_code as string | undefined
      const discountType = meta.discount_type as string | undefined
      const amount = meta.amount as number | undefined
      const maxDiscount = meta.max_discount as number | undefined
      const parts: string[] = []
      if (code) parts.push(`Código: ${code}`)
      if (discountType === 'percent' && amount) {
        parts.push(`${amount}% dcto`)
        if (maxDiscount) parts.push(`(max $${maxDiscount.toLocaleString('es-CL')})`)
      } else if (discountType === 'fixed_cart' && amount) {
        parts.push(`$${amount.toLocaleString('es-CL')} dcto`)
      }
      return parts.join(' - ')
    }
    case 'physical_gift_voucher': {
      const code = meta.voucher_code as string | undefined
      const desc = meta.gift_description as string | undefined
      const parts: string[] = []
      if (code) parts.push(`Vale: ${code}`)
      if (desc) parts.push(desc)
      return parts.join(' - ')
    }
    case 'free_shipping_flag': {
      const condition = meta.condition as string | undefined
      const minAmount = meta.min_amount as number | undefined
      if (condition === 'permanent') return 'Despacho gratis permanente'
      if (condition === 'min_amount' && minAmount) return `Despacho gratis (min $${minAmount.toLocaleString('es-CL')})`
      return 'Despacho gratis'
    }
    case 'points_bonus': {
      const points = meta.bonus_points as number | undefined
      return points ? `+${points} puntos bonus` : ''
    }
    case 'email_notification':
      return (meta.notified as boolean) ? 'Notificado' : 'Pendiente'
    default:
      return ''
  }
}

function getCategoryBadgeStyle(category: string): string {
  switch (category.toLowerCase()) {
    case 'oro':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    case 'marmol':
      return 'bg-gray-100 text-gray-700 border-gray-300'
    case 'bronce':
      return 'bg-orange-100 text-orange-800 border-orange-300'
    default:
      return 'bg-blue-100 text-blue-800 border-blue-300'
  }
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface ClientBenefitsSectionProps {
  allBenefits: ClientBenefitHistoryItem[]
  vouchers: BenefitVoucher[]
  activeCount: number
  usedCount: number
  expiredCount: number
  availableTiers: AvailableBenefitTier[]
  clientCategory: string | null
  isLoading: boolean
  availableLoading: boolean
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ClientBenefitsSection({
  allBenefits,
  vouchers,
  activeCount,
  usedCount,
  expiredCount,
  availableTiers,
  clientCategory,
  isLoading,
  availableLoading,
}: ClientBenefitsSectionProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-8 bg-gray-100 rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse h-20 bg-gray-100 rounded-lg" />
          ))}
        </div>
        <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
      </div>
    )
  }

  const pendingVouchers = vouchers.filter((v) => v.status === 'pending')

  return (
    <div className="space-y-6">
      {/* Section title */}
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <Award size={22} className="text-primary-600" />
        Beneficios del Cliente
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Activos</p>
          <p className="text-3xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Usados</p>
          <p className="text-3xl font-bold text-blue-600">{usedCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Expirados</p>
          <p className="text-3xl font-bold text-gray-500">{expiredCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Vouchers pendientes</p>
          <p className="text-3xl font-bold text-yellow-600">{pendingVouchers.length}</p>
        </div>
      </div>

      {/* All benefits table */}
      <div className="card">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Todos los beneficios</h3>
          <p className="text-sm text-gray-500 mt-1">
            Historial completo de beneficios otorgados al cliente
          </p>
        </div>
        {allBenefits.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Este cliente no tiene beneficios registrados
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Beneficio</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Detalle</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Estado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Otorgado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Expira</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Usado</th>
                </tr>
              </thead>
              <tbody>
                {allBenefits.map((benefit) => (
                  <tr key={benefit.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {getActionIcon(benefit.action_type)}
                        <span className="text-xs text-gray-600">
                          {ACTION_TYPE_LABELS[benefit.action_type]}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{benefit.rule_name}</p>
                        {benefit.rule_description && (
                          <p className="text-xs text-gray-500 mt-0.5">{benefit.rule_description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {getMetadataDisplay(benefit)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BenefitStatusBadge status={benefit.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(benefit.granted_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(benefit.expires_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(benefit.used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vouchers table */}
      {vouchers.length > 0 && (
        <div className="card">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Ticket size={18} className="text-primary-600" />
              Vouchers
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Vales físicos y códigos asociados al cliente
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Código</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Descripción</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Estado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Válido desde</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Válido hasta</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Canjeado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Sucursal canje</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Producto</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Monto</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">
                        {voucher.voucher_code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{voucher.voucher_type}</td>
                    <td className="px-4 py-3 text-gray-600">{voucher.gift_description || '-'}</td>
                    <td className="px-4 py-3">
                      <BenefitStatusBadge status={voucher.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(voucher.valid_from)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(voucher.valid_until)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {voucher.redeemed_at ? (
                        <div>
                          <p>{formatDateTime(voucher.redeemed_at)}</p>
                          {voucher.redeemed_by && (
                            <p className="text-xs text-gray-400">por {voucher.redeemed_by.name}</p>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {voucher.redeemed_at_branch?.address || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{voucher.redeemed_product || '-'}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {voucher.redeemed_amount
                        ? `$${voucher.redeemed_amount.toLocaleString('es-CL')}`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Available benefits by tier */}
      <div className="card">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Beneficios disponibles por categoría</h3>
          <p className="text-sm text-gray-500 mt-1">
            Catálogo de beneficios del programa según la categoría del cliente
          </p>
        </div>
        {availableLoading ? (
          <div className="animate-pulse p-4 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        ) : availableTiers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay beneficios configurados en el programa
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {availableTiers.map((tier) => {
              const isClientTier = tier.category.toLowerCase() === (clientCategory ?? '').toLowerCase()
              return (
                <div
                  key={tier.category}
                  className={`rounded-lg border p-4 ${
                    isClientTier ? 'border-primary-300 bg-primary-50/30 ring-1 ring-primary-200' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getCategoryBadgeStyle(tier.category)}`}
                    >
                      {tier.category_display_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      Umbral: {tier.threshold.toLocaleString('es-CL')} pts
                    </span>
                    {isClientTier && (
                      <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                        Categoría actual
                      </span>
                    )}
                  </div>
                  {tier.benefits.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin beneficios configurados para esta categoría</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tier.benefits.map((b) => (
                        <div
                          key={b.rule_id}
                          className="flex items-start gap-2 p-2 rounded bg-white border border-gray-100"
                        >
                          {getActionIcon(b.action_type)}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{b.name}</p>
                            {b.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{b.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">
                              {ACTION_TYPE_LABELS[b.action_type]}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
